import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

export type CalculationMethod = "PERCENTAGE" | "FLAT" | "TIERED";
export type PolicyStatus = "DRAFT" | "SCHEDULED" | "ACTIVE" | "SUSPENDED" | "ARCHIVED";
export type PolicyScope = "DEFAULT" | "NAMED";

export interface FeePolicyTier {
  fromAmount: string;
  toAmount: string | null;
  rate: string;
}

export interface FeePolicyDraft {
  scope?: PolicyScope;
  name?: string | null;
  calculationMethod: CalculationMethod;
  feeRate?: string | null;
  flatFeeAmount?: string | null;
  tierConfig?: FeePolicyTier[] | null;
  minFeeAmount?: string | null;
  maxFeeAmount?: string | null;
  feeBasis: "ORDER_TOTAL" | "PAYMENT" | "NET" | "SUBTOTAL_EX_TAX";
  deductionMode: "AUTO_DEDUCT" | "SEPARATE_INVOICE" | "OFFSET_PAYOUT" | "EXTERNAL";
  refundPolicy: "PRO_RATA" | "NONE" | "FULL_ONLY";
  absorbPlatformFee?: boolean;
  chargeEndCustomerFee?: boolean;
  feeCurrency: string;
  applicableOrderTypes?: string[];
  applicableCurrencies?: string[];
  excludedTradingRelationshipIds?: string[];
  effectiveUntil?: string | null;
  // Amend working-copy link: set when this DRAFT edits a live policy. On activation
  // the backend merges this draft's config back onto the source row (stable id).
  sourcePolicyId?: string | null;
}

/** Effective platform transaction-fee rate for the caller tenant (B10). */
export interface MyPlatformRate {
  feeRate: string | null;
  txFeeMinAmount: string | null;
  txFeeMaxAmount: string | null;
  source: "CONTRACT" | "NONE";
  currency: string;
}

export interface FeePolicyVersion {
  id: string;
  version: number;
  effectiveFrom: string;
  effectiveUntil: string | null;
  calculationMethod?: CalculationMethod;
  feeRate?: string | null;
  flatFeeAmount?: string | null;
}

export interface FeePolicy extends FeePolicyDraft {
  id: string;
  tenantId: string;
  status: PolicyStatus;
  rowVersion: number;
  effectiveFrom: string | null;
  currentVersion: number;
  versions?: FeePolicyVersion[];
  // Admin list enrichment (GET /bil/admin/tenants/:id/fee-policies):
  assignedCustomerCount?: number;       // active assignments to this policy
  defaultUsedByCount?: number;          // DEFAULT only: end-customers falling back to DEFAULT
}

export const list = (params?: { status?: PolicyStatus; scope?: PolicyScope; page?: number; limit?: number }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/bil/my/fee-policies", params as any);

export const get = (id: string) => httpRequest.get(`/bil/my/fee-policies/${id}`);

export const createDraft = (body: FeePolicyDraft) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post("/bil/my/fee-policies/draft", body as any);

export const saveDraft = (id: string, body: FeePolicyDraft & { rowVersion: number }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.put(`/bil/my/fee-policies/draft/${id}`, body as any);

export const activate = (id: string, rowVersion: number, effectiveFrom: string, effectiveUntil?: string | null) =>
  httpRequest.post(`/bil/my/fee-policies/draft/${id}/activate`, { rowVersion, effectiveFrom, effectiveUntil });

/** B10: tenant's effective platform fee rate, for the editor preview breakdown. */
export const getPlatformRate = (currency: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/bil/my/platform-fee-rate", { currency } as any);

export const suspend = (id: string, rowVersion: number) =>
  httpRequest.post(`/bil/my/fee-policies/${id}/suspend`, { rowVersion });

export const resume = (id: string, rowVersion: number) =>
  httpRequest.post(`/bil/my/fee-policies/${id}/resume`, { rowVersion });

export const cancelScheduled = (id: string, rowVersion: number) =>
  httpRequest.post(`/bil/my/fee-policies/scheduled/${id}/cancel`, { rowVersion });

export const cancelDraft = (id: string, rowVersion: number) =>
  httpRequest.post(`/bil/my/fee-policies/draft/${id}/cancel`, { rowVersion });
